/**
 * Background renewal of the console session
 * (specs/20260916-cortex-console-session-keep-alive design.md section 5.4).
 *
 * One timer, module-level, started when the provider holds an account and stopped on
 * sign-out or release. Each tick asks the caller-supplied ``renew`` for a fresh token;
 * a non-null result resets the interval to its base, a null or a throw doubles it up
 * to the ceiling. This module imports constants only - it cannot navigate, so a
 * background failure can never bounce the operator to Microsoft mid-task.
 */
import {
  CONSOLE_KEEP_ALIVE_BASE_MS,
  CONSOLE_KEEP_ALIVE_MAX_BACKOFF_MS,
} from "./sessionConstants";

export type KeepAliveRenew = () => Promise<string | null>;

let timer: ReturnType<typeof setTimeout> | null = null;
let delayMs = CONSOLE_KEEP_ALIVE_BASE_MS;
let activeRenew: KeepAliveRenew | null = null;

function schedule(renew: KeepAliveRenew): void {
  timer = setTimeout(() => {
    timer = null;
    void tick(renew);
  }, delayMs);
}

async function tick(renew: KeepAliveRenew): Promise<void> {
  if (activeRenew !== renew) {
    return; // stopped or restarted while this tick was pending
  }
  let renewed: string | null = null;
  try {
    renewed = await renew();
  } catch {
    renewed = null;
  }
  if (activeRenew !== renew) {
    return;
  }
  if (renewed) {
    delayMs = CONSOLE_KEEP_ALIVE_BASE_MS;
  } else {
    delayMs = Math.min(delayMs * 2, CONSOLE_KEEP_ALIVE_MAX_BACKOFF_MS);
    console.warn(`[console keep-alive] renewal failed; next attempt in ${delayMs}ms`);
  }
  schedule(renew);
}

/** Start (or restart) the keep-alive with a fresh base interval. */
export function startConsoleKeepAlive(renew: KeepAliveRenew): void {
  stopConsoleKeepAlive();
  activeRenew = renew;
  delayMs = CONSOLE_KEEP_ALIVE_BASE_MS;
  schedule(renew);
}

/** Stop the keep-alive; a tick already in flight discards its result. */
export function stopConsoleKeepAlive(): void {
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
  activeRenew = null;
}

/** The interval the next tick will wait (exported for tests). */
export function currentKeepAliveDelayMs(): number {
  return delayMs;
}

/** ``true`` while a tick is scheduled (exported for tests). */
export function isConsoleKeepAliveRunning(): boolean {
  return timer !== null || activeRenew !== null;
}
